"use client";

import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import styled from "@mui/material/styles/styled"; // GLOBAL CUSTOM COMPONENTS
import Image from "next/image";
import Link from "next/link";
import React, { useEffect } from "react";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import DefaultCard from "src/lib/components/salify-next-layout/default/components/Card";
import { H1, Paragraph } from "src/lib/components/salify-next-layout/default/components/Typography";
import clientFetcher from "src/lib/fetcher/client";
import { useGlobalContext } from "../salify-next-layout/default/global-context";
import { Box, CircularProgress } from "@mui/material";

const Wrapper = styled(DefaultCard)({
  margin: "auto",
  padding: "3rem",
  maxWidth: "630px",
  textAlign: "center",
});
const StyledButton = styled(Button)({
  marginTop: "2rem",
  padding: "11px 24px",
});

export interface VariantProps {
  /** @default "/" */
  browserProductURL?: string;
  /** @default "/cart/review" */
  cartCheckoutURL?: string;
  /** @default "/orders/{code}" */
  orderDetailURL?: string;
}

const OrderConfirmationPageView = (props: VariantProps) => {
  const { cleanCart } = useGlobalContext();
  const [orderConfirmState, setOrderConfirmState] = React.useState<{
    loading: boolean;
    isSuccess: boolean;
    code?: string;
    token?: string;
    qrCode?: string;
  }>({ loading: true, qrCode: "", isSuccess: false });

  useEffect(() => {
    (async () => {
      const res = await clientFetcher.processOrderConfirmation();
      if (res.code) cleanCart();
      setOrderConfirmState({ loading: false, ...res });
    })();
  }, []);

  return (
    <Container key={String(orderConfirmState.isSuccess)} sx={{ mt: 4, mb: 20 }}>
      {orderConfirmState.loading ? (
        <Box className="flex items-center">
          <Box className="contents">
            <CircularProgress style={{ margin: "auto" }} />
          </Box>
        </Box>
      ) : (
        <Wrapper>
          <Image
            width={116}
            height={116}
            className="inline"
            alt="complete"
            src={
              orderConfirmState.isSuccess
                ? "/assets/images/illustrations/party-popper.svg"
                : "/assets/images/illustrations/payment-failed.svg"
            }
          />
          <H1 lineHeight={1.1} mt="1.5rem">
            {orderConfirmState.isSuccess ? (
              <SalifyFallback.Label path="title" default="Đơn hàng của bạn đã hoàn tất!" />
            ) : (
              <SalifyFallback.Label path="titleFailed" default="Đơn hàng của bạn chưa hoàn tất!" />
            )}
          </H1>
          <Paragraph color="grey.800" mt="0.3rem">
            {orderConfirmState.isSuccess ? (
              <SalifyFallback.Label
                path="description"
                default="Bạn sẽ nhận được email xác nhận với chi tiết đơn hàng."
              />
            ) : (
              <SalifyFallback.Label
                path="descriptionFailed"
                default="Hãy quay lại trang thanh toán và thử lại."
              />
            )}
          </Paragraph>
          {orderConfirmState.qrCode ? (
            <div className="flex flex-col items-center">
              <img
                style={{ maxHeight: 200 }}
                src={`data:image/png;base64, ${orderConfirmState.qrCode}`}
              />
              <SalifyFallback.Label
                path="qrCodeDescription"
                default="Quét để xem chi tiết đơn hàng."
              />
              <StyledButton
                color="primary"
                disableElevation
                variant="outlined"
                className="button-link"
                LinkComponent={Link}
                href={(props.orderDetailURL ?? "/orders/{code}?token={token}")
                  .replace("{code}", orderConfirmState.code ?? "")
                  .replace("{token}", orderConfirmState.token ?? "")}
              >
                <SalifyFallback.Label path="Go To Order Details" default="Xem chi tiết đơn hàng" />
              </StyledButton>
            </div>
          ) : null}
          <StyledButton
            color="primary"
            disableElevation
            variant="contained"
            className="button-link"
            LinkComponent={Link}
            href={
              orderConfirmState.isSuccess
                ? (props.browserProductURL ?? "/")
                : (props.cartCheckoutURL ?? "/cart/review")
            }
          >
            {orderConfirmState.isSuccess ? (
              <SalifyFallback.Label path="Browse products" default="Tìm kiếm sản phẩm" />
            ) : (
              <SalifyFallback.Label path="Back to Checkout" default="Quay lại trang thanh toán" />
            )}
          </StyledButton>
        </Wrapper>
      )}
    </Container>
  );
};

export default OrderConfirmationPageView;
